import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, Send, X, Loader2, ExternalLink, LayoutDashboard, Settings } from 'lucide-react';
import { chatAICompanion, chatAdminCompanion } from '../../../services/openai';
import CompanionMarkdown from './CompanionMarkdown';
import ApiService from '../../../services/api';
import {
  companionChatStorageKey,
  purgeLegacyCompanionChatStorage,
} from '../../../utils/companionChatStorage';

const DEFAULT_STARTERS = [
  'How many test cases do we have?',
  'Generate scenarios from our existing test cases',
  'Where are our coverage gaps?',
  'Which test runs failed recently?',
];

const ADMIN_STARTERS = [
  'Summarize visitors in the last 30 days',
  'Which countries drive the most traffic?',
  'List tenants and their plans',
  'Show recent admin audit activity',
];

const ONBOARDING_STARTERS = [
  'Guide me step by step',
  'I\'ll describe my product — set everything up for me',
  'Create a sample project so I can explore',
];

const ONBOARDING_WELCOME = [
  "Welcome to **Testera** — I'm your AI Companion, and I'll help you get set up.",
  '',
  'We can go **step by step**. I\'ll explain each one and ask how you want it configured:',
  '1. **First project** — the product or area you\'re testing',
  '2. **Test plan** — scope, approach, and goals',
  '3. **Test cases** — concrete scenarios and steps',
  '4. **Test run** — an execution you can start',
  '5. **Connect MCPs** — GitHub, Jira, Notion, and other tools for Companion',
  '6. **Invite teammates** — Editors and Viewers for your workspace',
  '',
  'Prefer not to go step by step? **Describe what you want** (product, users, risk areas, stack) and I\'ll do a **best-effort setup** — project, plan, cases, and a run — in one go.',
  '',
  'How would you like to start?',
].join('\n');

function ArtifactOpenButtons({ artifacts, onOpenArtifact }) {
  if (!Array.isArray(artifacts) || !artifacts.length || typeof onOpenArtifact !== 'function') return null;
  return (
    <div className="mt-3 pt-2.5 border-t border-border/70 space-y-1.5">
      {artifacts.map((a) => (
        <button
          key={a.id}
          type="button"
          onClick={() => onOpenArtifact(a)}
          className="w-full flex items-center gap-2 text-left text-xs px-2.5 py-2 rounded-lg border border-teal-600/40 bg-teal-600/10 text-teal-200 hover:bg-teal-600/20 transition"
        >
          <LayoutDashboard className="w-3.5 h-3.5 shrink-0" />
          <span className="flex-1 min-w-0 truncate">
            Open “{a.title || 'Artifact'}” in Artifacts
          </span>
          <ExternalLink className="w-3.5 h-3.5 shrink-0 opacity-70" />
        </button>
      ))}
    </div>
  );
}

function loadStoredMessages(key) {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.slice(-40) : [];
  } catch {
    return [];
  }
}

function persistMessages(key, msgs) {
  try {
    sessionStorage.setItem(key, JSON.stringify((msgs || []).slice(-40)));
  } catch { /* ignore quota */ }
}

const AICompanionModal = ({
  isOpen,
  onClose,
  onMutated,
  onOpenArtifact,
  mode = 'workspace', // 'workspace' | 'admin'
  variant = 'dock', // 'dock' | 'onboarding'
  title,
  subtitle,
  starters,
  storageKey,
  tenantId,
  userId,
}) => {
  const isAdmin = mode === 'admin';
  const isOnboarding = variant === 'onboarding' && !isAdmin;
  const chatKey = storageKey
    || companionChatStorageKey({
      mode: isAdmin ? 'admin' : 'workspace',
      variant: isOnboarding ? 'onboarding' : variant,
      tenantId,
      userId,
    });
  const starterList = starters
    || (isOnboarding ? ONBOARDING_STARTERS : (isAdmin ? ADMIN_STARTERS : DEFAULT_STARTERS));
  const heading = title
    || (isOnboarding ? 'AI Companion — Get set up' : (isAdmin ? 'Admin AI Companion' : 'AI Companion'));
  const sub = subtitle || (isOnboarding
    ? 'I’ll walk you through project → plan → cases → runs → MCPs → teammates'
    : (isAdmin
      ? 'Ask about tenants, visitors, audit logs, and ops'
      : 'Ask anything about your Testera workspace'));
  const [messages, setMessages] = useState(() => loadStoredMessages(chatKey));
  const [activeChatKey, setActiveChatKey] = useState(chatKey);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [credits, setCredits] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const seededOnboardingRef = useRef(false);

  const applyCredits = (b) => {
    if (!b || b.metered === false) {
      setCredits(null);
      return;
    }
    setCredits(b);
    try {
      window.dispatchEvent(new CustomEvent('ai-credits-updated', { detail: b }));
    } catch { /* ignore */ }
  };

  const refreshCredits = () => {
    ApiService.getAiCredits()
      .then(applyCredits)
      .catch(() => {});
  };

  // Hard reset transcript when tenant/user scope changes (never reuse another workspace's chat).
  useEffect(() => {
    purgeLegacyCompanionChatStorage();
    seededOnboardingRef.current = false;
    const loaded = loadStoredMessages(chatKey);
    setMessages(loaded);
    setActiveChatKey(chatKey);
    setError('');
    setInput('');
  }, [chatKey]);

  useEffect(() => {
    if (!isOpen) return;
    setError('');
    if (activeChatKey === chatKey) {
      setMessages(loadStoredMessages(chatKey));
    }
    setTimeout(() => inputRef.current?.focus(), 50);
    if (!isAdmin) refreshCredits();
    else setCredits(null);
  }, [isOpen, chatKey, isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seed a first-time onboarding welcome without spending an AI turn
  useEffect(() => {
    if (!isOpen || !isOnboarding || seededOnboardingRef.current) return;
    if (activeChatKey !== chatKey) return;
    setMessages((prev) => {
      const existing = prev.length ? prev : loadStoredMessages(chatKey);
      if (existing.length > 0) {
        seededOnboardingRef.current = true;
        return existing;
      }
      const seeded = [{ role: 'assistant', content: ONBOARDING_WELCOME }];
      persistMessages(chatKey, seeded);
      seededOnboardingRef.current = true;
      return seeded;
    });
  }, [isOpen, isOnboarding, chatKey, activeChatKey]);

  useEffect(() => {
    // Never persist previous-tenant messages into a newly scoped key.
    if (activeChatKey !== chatKey) return;
    persistMessages(chatKey, messages);
  }, [messages, chatKey, activeChatKey]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, sending, isOpen]);

  const openArtifact = (artifact) => {
    if (typeof onOpenArtifact === 'function') onOpenArtifact(artifact);
    if (typeof onClose === 'function') onClose();
  };

  const clearChat = () => {
    setMessages(isOnboarding ? [{ role: 'assistant', content: ONBOARDING_WELCOME }] : []);
    setError('');
    persistMessages(chatKey, isOnboarding ? [{ role: 'assistant', content: ONBOARDING_WELCOME }] : []);
  };

  const send = async (text) => {
    const content = String(text || '').trim();
    if (!content || sending) return;
    setError('');
    const nextMessages = [...messages, { role: 'user', content }];
    setMessages(nextMessages);
    setInput('');
    setSending(true);
    try {
      const data = isAdmin
        ? await chatAdminCompanion(nextMessages)
        : await chatAICompanion(nextMessages, { onboarding: isOnboarding });
      const reply = String(data?.reply || '').trim() || 'I could not generate a reply.';
      const artifacts = Array.isArray(data?.artifacts) ? data.artifacts : [];
      setMessages((prev) => {
        const updated = [...prev, { role: 'assistant', content: reply, artifacts }];
        persistMessages(chatKey, updated);
        return updated;
      });
      if (!isAdmin) {
        if (data?.credits) applyCredits(data.credits);
        else refreshCredits();
      }
      // Refresh portal lists quietly AFTER the reply is committed — never full-page load.
      if (data?.mutated && typeof onMutated === 'function') {
        setTimeout(() => {
          Promise.resolve(onMutated()).catch(() => {});
        }, 0);
      }
    } catch (e) {
      const rawMsg = e?.message || 'AI Companion request failed';
      const isTimeout = /timeout|504|Gateway/i.test(rawMsg);
      const isServiceUnavailable = /503|overloaded|unavailable/i.test(rawMsg);
      let msg = rawMsg;
      if (isTimeout) {
        msg = 'The AI request timed out waiting for the upstream provider. Please try again with a specific question, or check Settings → AI to verify your provider.';
      } else if (isServiceUnavailable) {
        msg = 'The AI service is temporarily overloaded or unavailable. Please try again in a moment.';
      }
      setError(msg);
      setMessages((prev) => {
        const updated = [
          ...prev,
          { role: 'assistant', content: `Sorry — ${msg}` },
        ];
        persistMessages(chatKey, updated);
        return updated;
      });
      if (!isAdmin) refreshCredits();
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  const shellClass = isOnboarding
    ? 'fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-6 bg-black/60 backdrop-blur-md'
    : 'fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:justify-end p-0 sm:p-6 sm:pr-24 md:pr-28 bg-black/50 backdrop-blur-sm';

  const panelClass = isOnboarding
    ? 'w-full max-w-3xl h-[min(860px,94dvh)] app-glass flex flex-col overflow-hidden'
    : 'w-full sm:w-[460px] h-[min(760px,100dvh)] sm:h-[min(760px,92vh)] app-glass sm:rounded-2xl flex flex-col overflow-hidden';

  const bubbleTextClass = isOnboarding ? 'text-[15px] leading-relaxed' : 'text-sm leading-relaxed';

  return (
    <div className={shellClass}>
      <div className={panelClass} role="dialog" aria-modal="true" aria-label={heading}>
        <header className={`flex items-center justify-between gap-3 border-b border-border bg-surface-2/60 ${isOnboarding ? 'px-5 py-4' : 'px-4 py-3'}`}>
          <div className="flex items-center gap-2.5 min-w-0">
            <span className={`inline-flex items-center justify-center rounded-xl bg-teal-600/20 text-teal-400 ring-1 ring-teal-500/30 ${isOnboarding ? 'h-11 w-11' : 'h-9 w-9'}`}>
              <Sparkles className={isOnboarding ? 'w-6 h-6' : 'w-5 h-5'} />
            </span>
            <div className="min-w-0">
              <h3 className={`font-semibold text-text truncate ${isOnboarding ? 'text-lg' : 'text-sm'}`}>{heading}</h3>
              <p className={`text-text-muted truncate ${isOnboarding ? 'text-xs' : 'text-[11px]'}`}>{sub}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {credits && (
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent('ai-credits-open'))}
                className="text-[11px] px-2 py-1 rounded-full border border-teal-600/40 bg-teal-600/10 text-teal-300 hover:bg-teal-600/20 transition whitespace-nowrap"
                title="AI credits remaining this period — click to buy more"
              >
                {credits.remaining ?? 0} credits
              </button>
            )}
            {!isAdmin && (
              <button
                type="button"
                onClick={() => {
                  try {
                    window.dispatchEvent(new CustomEvent('open-settings', { detail: { tab: 'ai' } }));
                  } catch {}
                }}
                className="p-1.5 rounded-lg text-text-muted hover:text-text hover:bg-surface-3 transition"
                title="Configure AI Provider & Model (Settings → AI Provider)"
                aria-label="AI Provider Settings"
              >
                <Settings className="w-3.5 h-3.5" />
              </button>
            )}
            {messages.length > 0 && (
              <button
                type="button"
                onClick={clearChat}
                className="text-[11px] px-2 py-1 rounded-lg text-text-muted hover:text-text hover:bg-surface-3 transition"
                title="Clear conversation"
              >
                Clear
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg text-text-muted hover:text-text hover:bg-surface-3 transition"
              aria-label="Close AI Companion"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </header>

        <div className={`flex-1 overflow-y-auto space-y-3 ${isOnboarding ? 'px-5 py-5' : 'px-4 py-4'}`}>
          {messages.length === 0 && (
            <div className="space-y-3">
              <p className={`${isOnboarding ? 'text-base' : 'text-sm'} text-text-muted leading-relaxed`}>
                {isAdmin
                  ? 'I can look up tenants, visitor analytics, audit logs, and system ops status for the Admin Portal.'
                  : 'I can look up projects, test cases, plans, runs, tasks, and environments — or help you create them when you ask.'}
              </p>
              <div className="flex flex-wrap gap-2">
                {starterList.map((s) => (
                  <button
                    key={s}
                    type="button"
                    disabled={sending}
                    onClick={() => send(s)}
                    className={`${isOnboarding ? 'text-sm px-3.5 py-2' : 'text-xs px-3 py-1.5'} rounded-full border border-border bg-surface-2 text-text-muted hover:text-text hover:bg-surface-3 transition`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, idx) => (
            <div
              key={`${m.role}-${idx}`}
              className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[95%] rounded-2xl px-3.5 py-2.5 ${bubbleTextClass} ${
                  m.role === 'user'
                    ? 'bg-primary text-primary-foreground rounded-br-md'
                    : 'bg-surface-2 border border-border text-text rounded-bl-md'
                }`}
              >
                {m.role === 'assistant' ? (
                  <>
                    <CompanionMarkdown content={m.content} onOpenArtifact={openArtifact} />
                    <ArtifactOpenButtons artifacts={m.artifacts} onOpenArtifact={openArtifact} />
                  </>
                ) : (
                  <div className="whitespace-pre-wrap">{m.content}</div>
                )}
              </div>
            </div>
          ))}

          {/* Show starters under the welcome message until the user has replied */}
          {isOnboarding && messages.length === 1 && messages[0]?.role === 'assistant' && !sending && (
            <div className="flex flex-wrap gap-2 pt-1">
              {starterList.map((s) => (
                <button
                  key={s}
                  type="button"
                  disabled={sending}
                  onClick={() => send(s)}
                  className="text-sm px-3.5 py-2 rounded-full border border-teal-600/40 bg-teal-600/10 text-teal-200 hover:bg-teal-600/20 transition"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          {sending && (
            <div className="flex justify-start">
              <div className={`inline-flex items-center gap-2 rounded-2xl rounded-bl-md px-3.5 py-2.5 ${bubbleTextClass} bg-surface-2 border border-border text-text-muted`}>
                <Loader2 className="w-4 h-4 animate-spin" />
                Thinking…
              </div>
            </div>
          )}
          <div ref={bottomRef} />
        </div>

        {error && (
          <div className="px-4 py-2 text-xs text-amber-300 border-t border-border bg-amber-500/10">
            {error}
          </div>
        )}

        {isOnboarding && (
          <div className="px-5 py-2 border-t border-border bg-surface-2/30 flex items-center justify-between gap-3">
            <p className="text-[11px] text-text-muted">
              You can reopen AI Companion anytime from the floating button.
            </p>
            <button
              type="button"
              onClick={onClose}
              className="text-xs px-3 py-1.5 rounded-lg border border-border text-text-muted hover:text-text hover:bg-surface-3 transition whitespace-nowrap"
            >
              Skip for now
            </button>
          </div>
        )}

        <form
          className={`border-t border-border flex items-end gap-2 bg-surface-2/40 ${isOnboarding ? 'p-4' : 'p-3'}`}
          onSubmit={(e) => {
            e.preventDefault();
            send(input);
          }}
        >
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send(input);
              }
            }}
            rows={isOnboarding ? 3 : 2}
            placeholder={isOnboarding ? 'Describe your product, or tell me which step to start…' : 'Ask AI Companion…'}
            className={`flex-1 resize-none rounded-xl border border-border bg-surface-1 px-3 py-2 text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-primary/40 ${isOnboarding ? 'text-[15px]' : 'text-sm'}`}
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!input.trim() || sending}
            className={`inline-flex items-center justify-center rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 transition shrink-0 ${isOnboarding ? 'h-12 w-12' : 'h-10 w-10'}`}
            aria-label="Send message"
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AICompanionModal;
